import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { Entitlements } from '@snap-platform/shared';
import { useAuth } from '../context/AuthContext.js';
import { useSnap } from '../hooks/useSnap.js';
import {
  requestEmailRecovery,
  verifyEmailRecovery,
  getEntitlements,
  getSubscriptionStatus,
} from '../services/api.js';
import { trackConversion } from '../services/conversionTracking.js';
import {
  ShieldCheckIcon,
  CheckCircleIcon,
  AlertTriangleIcon,
  LockIcon,
  WalletIcon,
  LifeBuoyIcon,
  PlugIcon,
} from '../components/Icons.js';

// ---------------------------------------------------------------------------
// Status dot (reused pattern from Support page)
// ---------------------------------------------------------------------------
function StatusDot({ status }: { status: 'green' | 'yellow' | 'red' }) {
  return <span className={`sp-status-dot sp-status-dot-${status}`} aria-hidden="true" />;
}

// ---------------------------------------------------------------------------
// Step indicator
// ---------------------------------------------------------------------------
function StepIndicator({
  number,
  label,
  active,
  done,
}: {
  number: number;
  label: string;
  active: boolean;
  done: boolean;
}) {
  const className = done
    ? 'rc-step rc-step-done'
    : active
      ? 'rc-step rc-step-active'
      : 'rc-step';

  return (
    <div className={className}>
      <span className="rc-step-num">
        {done ? '\u2713' : number}
      </span>
      <span className="rc-step-label">{label}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type RecoverStep =
  | 'connect'
  | 'choose_action'
  | 'enter_email'
  | 'sending'
  | 'enter_code'
  | 'verifying'
  | 'success'
  | 'not_found'
  | 'error';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export function RecoverSubscription() {
  const { token, walletAddress, isAuthenticated, connectWallet, isConnecting } = useAuth();
  const snap = useSnap(token);
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState<RecoverStep>(isAuthenticated ? 'choose_action' : 'connect');
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [action, setAction] = useState<'restore' | 'transfer'>('restore');
  const [sessionId, setSessionId] = useState<string | null>(searchParams.get('sessionId'));
  const [result, setResult] = useState<{
    tier: string;
    stripeSubscriptionId?: string;
    action: string;
  } | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Live entitlements & subscription state
  const [entitlements, setEntitlements] = useState<Entitlements | null>(null);
  const [subStatus, setSubStatus] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    getEntitlements(token).then(setEntitlements).catch(() => {});
    getSubscriptionStatus(token).then((s) => setSubStatus(s.status)).catch(() => {});
  }, [token]);

  // Handle magic link redirect
  useEffect(() => {
    if (searchParams.get('recovery') === '1' && searchParams.get('sessionId')) {
      setSessionId(searchParams.get('sessionId'));
      if (isAuthenticated) {
        setStep('enter_code');
      }
    }
    if (searchParams.get('error') === 'expired') {
      setError('Recovery link has expired. Please request a new one.');
      setStep('error');
    }
  }, [searchParams, isAuthenticated]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  async function handleConnect() {
    setError(null);
    try {
      await connectWallet();
      setStep('choose_action');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to connect wallet');
    }
  }

  async function handleRequestCode() {
    if (!token || !email) return;
    trackConversion('recovery_started', { action });
    setStep('sending');
    setError(null);
    try {
      const res = await requestEmailRecovery(token, email, action);
      setSessionId(res.sessionId);
      setStep('enter_code');
      setResendCooldown(60);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send verification code');
      setStep('enter_email');
    }
  }

  async function handleResendCode() {
    if (!token || !email || resendCooldown > 0) return;
    setError(null);
    try {
      const res = await requestEmailRecovery(token, email, action);
      setSessionId(res.sessionId);
      setResendCooldown(60);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend code');
    }
  }

  async function handleVerifyCode() {
    if (!token || !code || !sessionId) return;
    setStep('verifying');
    setError(null);
    try {
      const res = await verifyEmailRecovery(token, sessionId, code);
      if (res.restored) {
        setResult({
          tier: res.tier,
          stripeSubscriptionId: res.stripeSubscriptionId,
          action: res.action,
        });
        // Refresh entitlements after successful restore
        if (token) {
          getEntitlements(token).then(setEntitlements).catch(() => {});
          setSubStatus('active');
        }
        setStep('success');
      } else {
        setStep('not_found');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Verification failed';
      setError(message);
      setStep('error');
    }
  }

  function truncateAddress(address: string): string {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  }

  function isValidEmail(value: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  // Derived state
  const isPro = entitlements?.tier === 'pro';
  const isSubActive = subStatus === 'active';
  const snapOk = snap.isInstalled;

  const stepNumber =
    step === 'connect'
      ? 1
      : step === 'choose_action'
        ? 2
        : step === 'enter_email' || step === 'sending'
          ? 3
          : step === 'enter_code' || step === 'verifying'
            ? 4
            : 5;

  const isTerminal = step === 'success' || step === 'not_found' || step === 'error';

  // Banner status
  const bannerStatus: 'green' | 'yellow' | 'red' = !isAuthenticated
    ? 'red'
    : isPro && isSubActive
      ? 'green'
      : 'yellow';

  return (
    <div className="recover-page">
      <div className="container rc-container">

        {/* ================================================================ */}
        {/* PART 1 — PROTECTION STATUS BANNER                               */}
        {/* ================================================================ */}
        <div className={`dash-card rc-banner rc-banner-${bannerStatus}`}>
          <div className="rc-banner-content">
            <ShieldCheckIcon className="rc-banner-icon" aria-hidden="true" />
            <div>
              <h3 className="rc-banner-title">Protection Status</h3>
              <p className="rc-banner-desc">
                {bannerStatus === 'green'
                  ? 'Your wallet is fully protected.'
                  : bannerStatus === 'yellow'
                    ? 'Limited protection. Restore your Pro subscription below.'
                    : 'Connect your wallet to check your protection status.'}
              </p>
            </div>
          </div>
          <div className="rc-banner-checks">
            <span className="rc-banner-check">
              <StatusDot status={isAuthenticated ? 'green' : 'red'} />
              Wallet {isAuthenticated ? 'Connected' : 'Not Connected'}
            </span>
            <span className="rc-banner-check">
              <StatusDot status={isPro && isSubActive ? 'green' : isPro ? 'yellow' : 'red'} />
              {isPro ? (isSubActive ? 'Pro Active' : 'Pro Inactive') : 'Free'}
            </span>
            <span className="rc-banner-check">
              <StatusDot status={snapOk ? 'green' : 'yellow'} />
              Snap {snapOk ? 'Installed' : 'Not Installed'}
            </span>
          </div>
        </div>

        {/* ================================================================ */}
        {/* PART 2 — EXPLANATION SECTION                                     */}
        {/* ================================================================ */}
        {!isTerminal && (
          <div className="dash-card">
            <div className="dash-card-header">
              <LifeBuoyIcon className="dash-section-icon" aria-hidden="true" />
              <h2>Restore Your Subscription</h2>
            </div>
            <p className="dash-card-subtitle">Use Restore when:</p>
            <ul className="rc-use-list">
              <li>You installed MetaMask on a new computer</li>
              <li>You reinstalled MetaMask</li>
              <li>Your Pro access disappeared</li>
            </ul>
            <p className="rc-reassurance">
              Your subscription is securely linked to your wallet and can be restored instantly. <strong>No payment required.</strong>
            </p>
          </div>
        )}

        {/* Error banner */}
        {error && !isTerminal && (
          <div className="rc-error-banner">
            <AlertTriangleIcon className="rc-error-banner-icon" aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        {/* Step indicators */}
        {!isTerminal && (
          <div className="rc-steps-row">
            <StepIndicator number={1} label="Connect" active={step === 'connect'} done={stepNumber > 1} />
            <StepIndicator number={2} label="Action" active={step === 'choose_action'} done={stepNumber > 2} />
            <StepIndicator number={3} label="Email" active={step === 'enter_email' || step === 'sending'} done={stepNumber > 3} />
            <StepIndicator number={4} label="Verify" active={step === 'enter_code' || step === 'verifying'} done={stepNumber > 4} />
          </div>
        )}

        {/* ================================================================ */}
        {/* PART 3 — PRIMARY RECOVERY CARD                                  */}
        {/* ================================================================ */}
        <div className="dash-card rc-card">
          {/* Step 1: Connect */}
          {step === 'connect' && (
            <>
              <div className="rc-card-step-label">Step 1</div>
              <h2 className="rc-card-title">Connect Your Wallet</h2>
              <p className="rc-card-desc">
                Connect the wallet you want to recover your subscription to.
              </p>
              <button className="btn btn-primary rc-btn-lg" onClick={handleConnect} disabled={isConnecting}>
                <WalletIcon className="rc-btn-icon" aria-hidden="true" />
                {isConnecting ? 'Connecting...' : 'Connect Wallet'}
              </button>
            </>
          )}

          {/* Step 2: Choose Action */}
          {step === 'choose_action' && (
            <>
              <div className="rc-card-step-label">Step 2</div>
              <h2 className="rc-card-title">Choose Recovery Action</h2>
              {walletAddress && (
                <p className="rc-card-wallet">
                  Connected: <code>{truncateAddress(walletAddress)}</code>
                </p>
              )}
              <div className="rc-action-choices">
                <label className={`rc-action-choice ${action === 'restore' ? 'rc-action-selected' : ''}`}>
                  <input
                    type="radio"
                    name="action"
                    value="restore"
                    checked={action === 'restore'}
                    onChange={() => setAction('restore')}
                  />
                  <div className="rc-action-text">
                    <strong>Restore</strong>
                    <span>Attach subscription to this wallet</span>
                  </div>
                </label>
                <label className={`rc-action-choice ${action === 'transfer' ? 'rc-action-selected' : ''}`}>
                  <input
                    type="radio"
                    name="action"
                    value="transfer"
                    checked={action === 'transfer'}
                    onChange={() => setAction('transfer')}
                  />
                  <div className="rc-action-text">
                    <strong>Transfer</strong>
                    <span>Move subscription from old wallet to this one</span>
                  </div>
                </label>
              </div>
              <button className="btn btn-primary rc-btn-lg" onClick={() => setStep('enter_email')}>
                Continue
              </button>
            </>
          )}

          {/* Step 3: Enter Email */}
          {step === 'enter_email' && (
            <>
              <div className="rc-card-step-label">Step 3</div>
              <h2 className="rc-card-title">Enter Your Billing Email</h2>
              <p className="rc-card-desc">
                Enter the email address you used for your Stripe subscription. We'll send a verification code if an active subscription is found.
              </p>
              <div className="rc-input-group">
                <input
                  type="email"
                  className="form-input"
                  placeholder="your-email@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <button
                className="btn btn-primary rc-btn-lg"
                onClick={handleRequestCode}
                disabled={!isValidEmail(email)}
              >
                Send Verification Code
              </button>
            </>
          )}

          {/* PART 8 — LOADING: Sending */}
          {step === 'sending' && (
            <div className="rc-loading">
              <div className="rc-spinner" />
              <h2 className="rc-card-title">Sending Verification Code...</h2>
              <p className="rc-card-desc">
                Checking for your subscription and sending a verification code. This may take a moment.
              </p>
            </div>
          )}

          {/* Step 4: Enter Code */}
          {step === 'enter_code' && (
            <>
              <div className="rc-card-step-label">Step 4</div>
              <h2 className="rc-card-title">Enter Verification Code</h2>
              <p className="rc-card-desc">
                If an active subscription was found, a 6-digit verification code has been sent to your email. Enter it below.
              </p>
              <div className="rc-input-group">
                <input
                  type="text"
                  className="form-input rc-code-input"
                  placeholder="000000"
                  value={code}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setCode(val);
                  }}
                  maxLength={6}
                />
              </div>
              <div className="rc-btn-row">
                <button
                  className="btn btn-primary rc-btn-lg"
                  onClick={handleVerifyCode}
                  disabled={code.length !== 6}
                >
                  Verify Code
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={handleResendCode}
                  disabled={resendCooldown > 0}
                >
                  {resendCooldown > 0 ? `Resend (${resendCooldown}s)` : 'Resend Code'}
                </button>
              </div>
            </>
          )}

          {/* PART 8 — LOADING: Verifying */}
          {step === 'verifying' && (
            <div className="rc-loading">
              <div className="rc-spinner" />
              <h2 className="rc-card-title">Restoring your protection...</h2>
              <p className="rc-card-desc">
                Verifying your code and restoring your subscription. Please wait.
              </p>
            </div>
          )}

          {/* ============================================================== */}
          {/* PART 4 — SUCCESS STATE                                         */}
          {/* ============================================================== */}
          {step === 'success' && result && (
            <div className="rc-result rc-result-success">
              <CheckCircleIcon className="rc-result-icon rc-result-icon-success" aria-hidden="true" />
              <h2 className="rc-card-title">Your Protection Has Been Restored</h2>
              <p className="rc-card-desc">
                Your subscription has been{' '}
                {result.action === 'transfer' ? 'transferred to' : 'recovered and linked to'} this wallet.
              </p>
              <div className="dash-kv-list rc-result-details">
                <div className="dash-kv-row">
                  <span className="dash-kv-label">Plan</span>
                  <span className="dash-kv-value sp-status-inline">
                    <StatusDot status="green" />
                    Pro
                  </span>
                </div>
                {entitlements && (
                  <div className="dash-kv-row">
                    <span className="dash-kv-label">Scans Remaining</span>
                    <span className="dash-kv-value">
                      {entitlements.scansRemaining} / {entitlements.monthlyLimit}
                    </span>
                  </div>
                )}
                <div className="dash-kv-row">
                  <span className="dash-kv-label">Snap Status</span>
                  <span className="dash-kv-value sp-status-inline">
                    <StatusDot status={snapOk ? 'green' : 'yellow'} />
                    {snapOk ? 'Installed' : 'Not Installed'}
                  </span>
                </div>
              </div>
              {!snapOk && snap.isMetaMask && (
                <button
                  className="btn btn-primary rc-btn-lg"
                  onClick={() => snap.installSnap()}
                  disabled={snap.isLoading}
                  style={{ marginBottom: '0.75rem' }}
                >
                  <PlugIcon className="rc-btn-icon" aria-hidden="true" />
                  {snap.isLoading ? 'Installing...' : 'Install Snap Now'}
                </button>
              )}
              <Link to="/wallet" className="btn btn-primary rc-btn-lg">
                Go to Dashboard
              </Link>
            </div>
          )}

          {/* ============================================================== */}
          {/* PART 5 — FAILURE STATE                                         */}
          {/* ============================================================== */}
          {step === 'not_found' && (
            <div className="rc-result rc-result-warning">
              <AlertTriangleIcon className="rc-result-icon rc-result-icon-warning" aria-hidden="true" />
              <h2 className="rc-card-title">No Active Subscription Found</h2>
              <p className="rc-card-desc">
                We could not find an active subscription associated with that email. This can happen if:
              </p>
              <ul className="rc-use-list rc-failure-list">
                <li>The subscription was cancelled</li>
                <li>The email address doesn't match your Stripe billing email</li>
                <li>The billing period has expired</li>
              </ul>
              <div className="rc-btn-row">
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setCode('');
                    setError(null);
                    setStep('enter_email');
                  }}
                >
                  Try Again
                </button>
                <Link to="/support" className="btn btn-secondary">
                  Contact Support
                </Link>
              </div>
            </div>
          )}

          {step === 'error' && (
            <div className="rc-result rc-result-error">
              <AlertTriangleIcon className="rc-result-icon rc-result-icon-warning" aria-hidden="true" />
              <h2 className="rc-card-title">Verification Failed</h2>
              <p className="rc-card-desc">
                {error || 'An error occurred during verification.'}
              </p>
              <div className="rc-btn-row">
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setCode('');
                    setError(null);
                    setStep('enter_code');
                  }}
                >
                  Retry Code
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setEmail('');
                    setCode('');
                    setError(null);
                    setStep('enter_email');
                  }}
                >
                  Start Over
                </button>
                <Link to="/support" className="btn btn-secondary">
                  Contact Support
                </Link>
              </div>
            </div>
          )}
        </div>

        {/* ================================================================ */}
        {/* PART 6 — TRANSFER INSTEAD?                                      */}
        {/* ================================================================ */}
        {!isTerminal && (
          <div className="dash-card rc-transfer-section">
            <div className="rc-transfer-divider" />
            <div className="dash-card-header">
              <WalletIcon className="dash-section-icon" aria-hidden="true" />
              <h2>Using a Different Wallet?</h2>
            </div>
            <p className="dash-card-subtitle">Use Transfer if:</p>
            <ul className="rc-use-list">
              <li>You created a new wallet</li>
              <li>You want to move your Pro plan to a different address</li>
            </ul>
            <Link to="/transfer" className="btn btn-secondary">
              Transfer Subscription
            </Link>
          </div>
        )}

        {/* ================================================================ */}
        {/* PART 7 — TRUST SECTION                                          */}
        {/* ================================================================ */}
        <div className="dash-card">
          <div className="dash-card-header">
            <LockIcon className="dash-section-icon" aria-hidden="true" />
            <h2>Recovery Security</h2>
          </div>
          <div className="rc-trust-grid">
            <div className="rc-trust-item">
              <ShieldCheckIcon className="rc-trust-icon" aria-hidden="true" />
              <div>
                <h4>Non-Custodial</h4>
                <p>CryptoGuardians never controls your funds or private keys.</p>
              </div>
            </div>
            <div className="rc-trust-item">
              <LockIcon className="rc-trust-icon" aria-hidden="true" />
              <div>
                <h4>Secure Recovery</h4>
                <p>Only the wallet owner can restore access via email verification.</p>
              </div>
            </div>
            <div className="rc-trust-item">
              <WalletIcon className="rc-trust-icon" aria-hidden="true" />
              <div>
                <h4>No Passwords</h4>
                <p>Your wallet is your identity. No passwords to remember or reset.</p>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
